import { KeyRound } from "lucide-react";
import { useRef } from "react";

import { NoticeDialog } from "@/components/notice-dialog";
import { Button } from "@/components/ui/button";

/**
 * The password-expiry warning, shown while the person can still act on it.
 *
 * No X. Both answers are already on the card and one of them *is* dismissing
 * it — a third way out that says neither is a choice people make by accident,
 * on the one notice worth reading before an account stops signing in.
 *
 * Every way out that remains — Esc, either button — reports through the
 * callbacks, so the caller decides where to go.
 */
export function ExpiryDialog({
  remaining,
  onLater,
  onChange,
}: {
  remaining: number;
  onLater: () => void;
  onChange: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const choseChange = useRef(false);
  const expired = remaining <= 0;

  return (
    <NoticeDialog
      dialogRef={ref}
      icon={KeyRound}
      title={expired ? "Password Expired" : "Password Expiry"}
      description={
        expired
          ? "Your password has expired. Change it now to keep signing in."
          : "Your password is about to expire. Please update your password."
      }
      extra={
        expired ? null : (
          <p className="text-warning text-callout mt-1 pt-2 font-medium">
            Days remaining: {remaining}
          </p>
        )
      }
      closable={false}
      // Every exit lands here — Esc, either button — so the route is decided
      // in one place. Esc reads as Remind Me Later, which is what it is.
      onClose={() => (choseChange.current ? onChange() : onLater())}
      actions={
        <>
          <Button variant="secondary" onClick={() => ref.current?.close()}>
            Remind Me Later
          </Button>
          <Button
            onClick={() => {
              choseChange.current = true;
              ref.current?.close();
            }}
          >
            Change Password
          </Button>
        </>
      }
    />
  );
}
